import { useState } from 'react';
import { Button } from '@/ui/components/ui/button';
import { Input } from '@/ui/components/ui/input';
import { Label } from '@/ui/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/ui/components/ui/select';
import { Textarea } from '@/ui/components/ui/textarea';
import {
	useAdminCreateProject,
	useAdminTenants,
	VOTING_MODES,
} from '../../hooks/use-admin';

interface CreateProjectFormProps {
	onSuccess: () => void;
	onCancel: () => void;
}

export function CreateProjectForm({
	onSuccess,
	onCancel,
}: CreateProjectFormProps) {
	const [createProject] = useAdminCreateProject();
	const { data: tenantsData } = useAdminTenants();
	const tenants = tenantsData?.adminTenants ?? [];
	const [formError, setFormError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [form, setForm] = useState({
		tenantId: '',
		name: '',
		description: '',
		votingMode: 'TRACKED',
	});

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setFormError(null);
		setSubmitting(true);
		try {
			await createProject({
				variables: {
					input: {
						tenantId: form.tenantId,
						name: form.name,
						description: form.description || undefined,
						votingMode: form.votingMode as
							| 'TRACKED'
							| 'OPEN'
							| 'COOKIE',
					},
				},
			});
			onSuccess();
		} catch (err) {
			setFormError(
				err instanceof Error ? err.message : 'Failed to create project',
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className='grid grid-cols-2 gap-4'>
			<div className='col-span-2 flex flex-col gap-1.5'>
				<Label htmlFor='name'>Name</Label>
				<Input
					id='name'
					required
					placeholder='My Product'
					value={form.name}
					onChange={(e) =>
						setForm((f) => ({ ...f, name: e.target.value }))
					}
				/>
			</div>
			<div className='flex flex-col gap-1.5'>
				<Label htmlFor='tenantId'>Tenant</Label>
				<Select
					value={form.tenantId}
					onValueChange={(v) =>
						setForm((f) => ({ ...f, tenantId: v }))
					}
					required
				>
					<SelectTrigger id='tenantId' className='w-full'>
						<SelectValue placeholder='Select a tenant…' />
					</SelectTrigger>
					<SelectContent>
						{tenants.map((t) => (
							<SelectItem key={t.id} value={t.id}>
								{t.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<div className='flex flex-col gap-1.5'>
				<Label htmlFor='votingMode'>Voting mode</Label>
				<Select
					value={form.votingMode}
					onValueChange={(v) =>
						setForm((f) => ({ ...f, votingMode: v }))
					}
				>
					<SelectTrigger id='votingMode' className='w-full'>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{VOTING_MODES.map((m) => (
							<SelectItem key={m} value={m}>
								{m.charAt(0) + m.slice(1).toLowerCase()}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<div className='col-span-2 flex flex-col gap-1.5'>
				<Label htmlFor='description'>Description</Label>
				<Textarea
					id='description'
					placeholder='Optional description'
					rows={3}
					value={form.description}
					onChange={(e) =>
						setForm((f) => ({ ...f, description: e.target.value }))
					}
				/>
			</div>
			<div className='col-span-2 flex flex-col gap-2'>
				{formError && (
					<p className='text-sm text-destructive rounded-xl bg-destructive/10 px-3.5 py-2.5'>
						{formError}
					</p>
				)}
				<div className='flex gap-2 justify-end'>
					<Button
						type='button'
						variant='outline'
						onClick={onCancel}
						disabled={submitting}
					>
						Cancel
					</Button>
					<Button type='submit' disabled={submitting}>
						{submitting ? 'Creating…' : 'Create project'}
					</Button>
				</div>
			</div>
		</form>
	);
}
